import {
  LEAD_SOURCES, PRIORITIES, PRIORITY_LABELS, SOURCE_LABELS, addDaysToDay, formatDate, formatILS, formatRelativeDay, israelDay,
  type LifecycleStage, type OrganizationListQuery,
} from '@takumi/shared';
import { ArrowDown, ChevronLeft, ChevronRight, Download, Plus, Search, Store, Users, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/form';
import { Badge, Card, EmptyState, Skeleton } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useOrgs, useStages, useUserOptions } from '@/lib/queries';
import type { OrgSummary, Paged } from '@/lib/types';
import { cn } from '@/lib/utils';
import { FollowupBadge, PriorityDot, StageBadge, urgencyAccent } from '@/features/crm/badges';
import { ContactActions } from '@/features/crm/ContactActions';
import { NewOrganizationDialog } from '@/features/organizations/NewLeadDialog';

type Sort = OrganizationListQuery['sort'];

const FOLLOWUP_FILTERS = [
  { key: '', label: 'הכל' },
  { key: 'overdue', label: 'באיחור' },
  { key: 'today', label: 'היום' },
  { key: 'upcoming', label: 'מתוכנן' },
  { key: 'none', label: 'ללא פולואפ' },
] as const;

const PERIODS = [
  { key: '', label: 'כל התאריכים' },
  { key: 'today', label: 'נרשמו היום' },
  { key: '7d', label: '7 ימים אחרונים' },
  { key: '30d', label: '30 ימים אחרונים' },
  { key: 'month', label: 'החודש' },
  { key: 'custom', label: 'טווח תאריכים…' },
] as const;

/** Filter keys kept in the URL (so a filtered view can be bookmarked or shared). */
const FILTER_KEYS = ['q', 'stageId', 'ownerId', 'source', 'priority', 'followup', 'status', 'period', 'from', 'to'] as const;

function periodRange(period: string | null, from: string | null, to: string | null): { registeredFrom?: string; registeredTo?: string } {
  const today = israelDay();
  switch (period) {
    case 'today': return { registeredFrom: today, registeredTo: today };
    case '7d': return { registeredFrom: addDaysToDay(today, -6), registeredTo: today };
    case '30d': return { registeredFrom: addDaysToDay(today, -29), registeredTo: today };
    case 'month': return { registeredFrom: `${today.slice(0, 8)}01`, registeredTo: today };
    case 'custom': return { ...(from ? { registeredFrom: from } : {}), ...(to ? { registeredTo: to } : {}) };
    default: return {};
  }
}

const isNew = (registeredAt: string) => Date.now() - new Date(registeredAt).getTime() < 48 * 3600_000;

export function OrganizationsPage({ lifecycle }: { lifecycle: Extract<LifecycleStage, 'lead' | 'customer'> }) {
  const isLeads = lifecycle === 'lead';
  const navigate = useNavigate();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [qInput, setQInput] = useState(params.get('q') ?? '');
  const stages = useStages();
  const users = useUserOptions();

  const defaultSort: Sort = isLeads ? 'registered' : 'name';
  const sort = (params.get('sort') as Sort | null) ?? defaultSort;
  // Leads: only active (open-stage) leads unless asked otherwise. Customers sit in the "won" stage, so no status filter.
  const status = isLeads ? (params.get('status') ?? 'open') : null;

  const query = useMemo(() => {
    const q: Partial<OrganizationListQuery> = {
      lifecycle,
      sort,
      page: Number(params.get('page') ?? 1),
      pageSize: 50,
      ...(status && status !== 'all' ? { status: status as 'open' | 'closed' } : {}),
      ...periodRange(params.get('period'), params.get('from'), params.get('to')),
    };
    for (const k of ['q', 'stageId', 'ownerId', 'source', 'priority', 'followup'] as const) {
      const v = params.get(k);
      if (v) (q as Record<string, string>)[k] = v;
    }
    return q;
  }, [params, lifecycle, sort, status]);

  const { data, isLoading, isFetching } = useOrgs(query);

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k !== 'page') next.delete('page');
    if (k === 'period' && v !== 'custom') {
      next.delete('from');
      next.delete('to');
    }
    setParams(next, { replace: true });
  };
  const activeFilters = FILTER_KEYS.filter((k) => k !== 'status' && params.get(k)).length + (isLeads && status !== 'open' ? 1 : 0);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const stageOptions = stages.data?.filter((s) => s.isActive && (status === 'open' ? s.kind === 'open' : status === 'closed' ? s.kind !== 'open' : true)) ?? [];
  const colCount = 9;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows: OrgSummary[] = [];
      for (let page = 1; page <= 50; page++) {
        const r = await api.get<Paged<OrgSummary>>('/organizations', { ...query, page, pageSize: 200 } as unknown as Record<string, string>);
        rows.push(...r.items);
        if (rows.length >= r.total || r.items.length === 0) break;
      }
      downloadCsv(`${isLeads ? 'לידים' : 'לקוחות'}-${israelDay()}.csv`, toCsv(rows, isLeads));
      toast.success(`יוצאו ${rows.length} רשומות`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const subtitle = data
    ? `${data.total.toLocaleString('he-IL')} ${isLeads ? (status === 'open' ? 'לידים פעילים' : status === 'closed' ? 'לידים סגורים' : 'לידים') : 'לקוחות'}${activeFilters ? ' בסינון הנוכחי' : ''}`
    : ' ';

  return (
    <div className="mx-auto max-w-[1400px]">
      <PageHeader
        title={isLeads ? 'לידים' : 'לקוחות'}
        subtitle={subtitle}
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => void exportCsv()} loading={exporting} disabled={!data?.total}>
              <Download /> ייצוא לאקסל
            </Button>
            {can('crm.write') && (
              <Button onClick={() => setCreating(true)}>
                <Plus /> {isLeads ? 'ליד חדש' : 'לקוח חדש'}
              </Button>
            )}
          </div>
        }
      />

      <Card className="mb-4 grid gap-2 p-3">
        {/* Row 1: search + registration period */}
        <div className="flex flex-wrap items-center gap-2">
          <form
            className="relative min-w-56 flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              setParam('q', qInput.trim() || null);
            }}
          >
            <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} onBlur={() => setParam('q', qInput.trim() || null)} placeholder="שם, עיר, טלפון, אימייל, ח.פ." className="ps-9" aria-label="חיפוש ברשימה" />
          </form>
          <Select value={params.get('period') ?? ''} onChange={(e) => setParam('period', e.target.value || null)} className="w-44" aria-label="תאריך הרשמה">
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.key ? p.label : 'תאריך הרשמה: הכל'}</option>)}
          </Select>
          {params.get('period') === 'custom' && (
            <div className="flex items-center gap-1.5 text-sm text-muted">
              <span>מ-</span>
              <Input type="date" value={params.get('from') ?? ''} onChange={(e) => setParam('from', e.target.value || null)} className="w-40" aria-label="מתאריך" />
              <span>עד</span>
              <Input type="date" value={params.get('to') ?? ''} onChange={(e) => setParam('to', e.target.value || null)} className="w-40" aria-label="עד תאריך" />
            </div>
          )}
        </div>

        {/* Row 2: follow-up state, status, stage, owner, source, priority */}
        <div className="flex flex-wrap items-center gap-2">
          {isLeads && (
            <div className="flex rounded-control border border-line-strong bg-sunken/50 p-0.5" role="group" aria-label="פולואפ">
              {FOLLOWUP_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setParam('followup', f.key || null)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-[13px] font-semibold',
                    (params.get('followup') ?? '') === f.key ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          {isLeads && (
            <Select value={status ?? 'open'} onChange={(e) => { setParam('status', e.target.value === 'open' ? null : e.target.value); }} className="w-36" aria-label="סטטוס">
              <option value="open">פעילים</option>
              <option value="closed">סגורים (לא רלוונטי / אבוד)</option>
              <option value="all">כל הלידים</option>
            </Select>
          )}
          {isLeads && (
            <Select value={params.get('stageId') ?? ''} onChange={(e) => setParam('stageId', e.target.value || null)} className="w-40" aria-label="שלב">
              <option value="">כל השלבים</option>
              {stageOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          )}
          <Select value={params.get('ownerId') ?? ''} onChange={(e) => setParam('ownerId', e.target.value || null)} className="w-36" aria-label="אחראי">
            <option value="">כל האחראים</option>
            <option value="me">שלי</option>
            <option value="none">לא משויך</option>
            {users.data?.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
          </Select>
          <Select value={params.get('source') ?? ''} onChange={(e) => setParam('source', e.target.value || null)} className="w-36" aria-label="מקור">
            <option value="">כל המקורות</option>
            {LEAD_SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
          </Select>
          <Select value={params.get('priority') ?? ''} onChange={(e) => setParam('priority', e.target.value || null)} className="w-32" aria-label="עדיפות">
            <option value="">כל העדיפויות</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
          </Select>
          <Select value={sort} onChange={(e) => setParam('sort', e.target.value === defaultSort ? null : e.target.value)} className="w-44" aria-label="מיון">
            {isLeads && <option value="registered">מיון: חדשים קודם</option>}
            <option value="next_followup">מיון: פולואפ הבא</option>
            {!isLeads && <option value="last_order">מיון: הזמנה אחרונה</option>}
            <option value="last_contact">מיון: קשר אחרון</option>
            <option value="value">מיון: שווי</option>
            <option value="name">מיון: שם</option>
          </Select>
          {(activeFilters > 0 || params.get('sort')) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQInput('');
                setParams({}, { replace: true });
              }}
            >
              <X /> ניקוי סינון
            </Button>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] table-fixed text-[14px]">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[10%]" />
              <col className="w-[13%]" />
              <col className="w-[11%]" />
              <col className="w-[13%]" />
              <col className="w-[9%]" />
              <col className="w-[9%]" />
              <col className="w-[7%]" />
              <col className="w-[6%]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-sunken text-xs font-bold text-muted">
              <tr className="[&>th]:px-3 [&>th]:py-2.5 [&>th]:text-start [&>th]:font-bold">
                <SortTh active={sort === 'name'} onClick={() => setParam('sort', 'name')} className="ps-5">עסק</SortTh>
                {isLeads ? (
                  <SortTh active={sort === 'registered'} onClick={() => setParam('sort', null)}>תאריך הרשמה</SortTh>
                ) : (
                  <SortTh active={sort === 'last_order'} onClick={() => setParam('sort', 'last_order')}>הזמנה אחרונה</SortTh>
                )}
                <th>איש קשר</th>
                <th>{isLeads ? 'שלב' : 'לקוח מאז'}</th>
                <SortTh active={sort === 'next_followup'} onClick={() => setParam('sort', 'next_followup')}>פולואפ הבא</SortTh>
                <SortTh active={sort === 'last_contact'} onClick={() => setParam('sort', 'last_contact')}>קשר אחרון</SortTh>
                <th>אחראי</th>
                <SortTh active={sort === 'value'} onClick={() => setParam('sort', 'value')} className="text-end">{isLeads ? 'שווי משוער' : 'צריכה חודשית'}</SortTh>
                <th><span className="sr-only">פעולות</span></th>
              </tr>
            </thead>
            <tbody className={cn('divide-y divide-line/70', isFetching && 'opacity-70')}>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={colCount} className="px-5 py-3">
                      <Skeleton className="h-5 w-full" />
                    </td>
                  </tr>
                ))}
              {data?.items.map((o) => (
                <tr key={o.id} onClick={() => navigate(`/org/${o.id}`)} className="cursor-pointer align-middle transition-colors hover:bg-matcha-50/50">
                  {/* The urgency strip lives on the first cell: a ::before on <tr> becomes an extra table cell and shifts the columns. */}
                  <td
                    className={cn(
                      'relative py-2.5 ps-5 pe-3',
                      'before:absolute before:inset-y-0 before:start-0 before:w-[3px]',
                      urgencyAccent(o.nextFollowupAt, o.stageKind === 'open'),
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <PriorityDot priority={o.priority} />
                      <span className="truncate font-semibold">{o.name}</span>
                      {isLeads && isNew(o.registeredAt) && <Badge variant="matcha" className="shrink-0">חדש</Badge>}
                    </div>
                    <div className="truncate text-xs text-muted">{[o.businessType, o.city, SOURCE_LABELS[o.source]].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 text-xs tabular">
                    {isLeads ? (
                      <>
                        <div>{formatDate(o.registeredAt)}</div>
                        <div className="text-muted">{formatRelativeDay(o.registeredAt, new Date(), false)}</div>
                      </>
                    ) : o.lastOrderDate ? (
                      <LastOrder day={o.lastOrderDate} />
                    ) : (
                      <span className="text-faint">אין הזמנות</span>
                    )}
                  </td>
                  <td className="px-3">
                    <div className="truncate">{o.contactName ?? <span className="text-faint">—</span>}</div>
                  </td>
                  <td className="px-3">{isLeads ? <StageBadge name={o.stageName} color={o.stageColor} /> : <span className="tabular">{formatDate(o.customerSince)}</span>}</td>
                  <td className="px-3">
                    <FollowupBadge dueAt={o.nextFollowupAt} title={o.nextFollowupTitle} showMissing={o.stageKind === 'open' || !isLeads} />
                  </td>
                  <td className="px-3 text-xs text-muted">{o.lastContactAt ? formatRelativeDay(o.lastContactAt, new Date(), false) : 'טרם'}</td>
                  <td className="truncate px-3 text-xs">{o.ownerName ?? <span className="text-faint">לא משויך</span>}</td>
                  <td className="px-3 text-end tabular">
                    {isLeads ? (o.estimatedValue ? formatILS(o.estimatedValue, { decimals: false }) : '—') : o.estimatedMonthlyKg ? `${o.estimatedMonthlyKg} ק״ג` : '—'}
                  </td>
                  <td className="px-3" onClick={(e) => e.stopPropagation()}>
                    <ContactActions phone={o.contactPhone} whatsapp={o.contactWhatsapp} email={o.contactEmail} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && data.items.length === 0 && (
          <EmptyState
            icon={isLeads ? <Users /> : <Store />}
            title={activeFilters ? 'לא נמצאו תוצאות לסינון הזה' : isLeads ? 'אין עדיין לידים' : 'אין עדיין לקוחות'}
            description={activeFilters ? 'נסו לשנות או לנקות את הסינון' : isLeads ? 'לידים מהאתר יופיעו כאן אוטומטית, או הוסיפו ליד ידנית' : 'לידים שהפכו ללקוחות יופיעו כאן'}
          />
        )}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between border-t border-line px-4 py-2 text-sm">
            <span className="text-muted">
              {(data.page - 1) * data.pageSize + 1}–{Math.min(data.page * data.pageSize, data.total)} מתוך {data.total.toLocaleString('he-IL')}
            </span>
            {pages > 1 && (
              <div className="flex items-center gap-1">
                <span className="me-2 text-muted">עמוד {data.page} מתוך {pages}</span>
                <Button variant="secondary" size="icon-sm" disabled={data.page <= 1} onClick={() => setParam('page', String(data.page - 1))} aria-label="הקודם">
                  <ChevronRight />
                </Button>
                <Button variant="secondary" size="icon-sm" disabled={data.page >= pages} onClick={() => setParam('page', String(data.page + 1))} aria-label="הבא">
                  <ChevronLeft />
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
      <NewOrganizationDialog open={creating} onOpenChange={setCreating} asCustomer={!isLeads} />
    </div>
  );
}

/** Column header that sorts the list (newest/highest first) when clicked. */
function SortTh({ active, onClick, className, children }: { active: boolean; onClick: () => void; className?: string; children: ReactNode }) {
  return (
    <th className={className} aria-sort={active ? 'descending' : undefined}>
      <button type="button" onClick={onClick} className={cn('inline-flex items-center gap-1 hover:text-ink', active && 'text-ink')}>
        {children}
        {active && <ArrowDown className="size-3" />}
      </button>
    </th>
  );
}

/** "12/09/2026 (לפני 5 ימים)"; the relative part only when it adds something (recent orders). */
function LastOrder({ day }: { day: string }) {
  const date = formatDate(day);
  const rel = formatRelativeDay(day, new Date(), false);
  return (
    <>
      <div>{date}</div>
      {rel !== date && <div className="text-muted">{rel}</div>}
    </>
  );
}

// ─── CSV export (opens in Excel with Hebrew intact) ─────────────────────────
function csvCell(v: unknown): string {
  let s = v === null || v === undefined ? '' : String(v);
  // Neutralise spreadsheet formulas in user-entered text (CSV injection).
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(rows: OrgSummary[], isLeads: boolean): string {
  const header = ['עסק', 'סוג עסק', 'עיר', 'מקור', isLeads ? 'תאריך הרשמה' : 'הזמנה אחרונה', isLeads ? 'שלב' : 'לקוח מאז', 'איש קשר', 'טלפון', 'אימייל', 'פולואפ הבא', 'קשר אחרון', 'אחראי', isLeads ? 'שווי משוער' : 'צריכה חודשית (ק״ג)'];
  const lines = rows.map((o) => [
    o.name, o.businessType, o.city, SOURCE_LABELS[o.source],
    isLeads ? formatDate(o.registeredAt) : formatDate(o.lastOrderDate),
    isLeads ? o.stageName : formatDate(o.customerSince),
    o.contactName, o.contactPhone, o.contactEmail,
    o.nextFollowupAt ? formatDate(o.nextFollowupAt) : '', o.lastContactAt ? formatDate(o.lastContactAt) : '', o.ownerName,
    isLeads ? o.estimatedValue : o.estimatedMonthlyKg,
  ].map(csvCell).join(','));
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
